import type { RiskClass, TrustLabel } from '@ralph/contracts'
import type { Messages } from '@/i18n/index.ts'
import type { BudgetDimension } from '@/i18n/messages/en/trace.ts'

/**
 * The logic that READS the trace catalogue, kept out of the catalogue (PRD-27 D-25, LOC-017).
 *
 * A messages file holds words and nothing else. The moment a lookup lives beside them, a
 * locale can acquire its own behaviour: a pt-BR file copied from the English one and then
 * edited would be free to fall back differently, or to recognise a different set of enum
 * members, and nothing would catch it. So the three fallback lookups live here, take the
 * active pack as a parameter, and are identical in every language by construction.
 *
 * All three exist because `run-stream-reducer.ts` widens `risk`, `label` and `dimension` to
 * `string` on the way in, so the panel has to look a value up rather than index it. Once the
 * reducer is narrowed back to the contract enums these guards are unreachable and the
 * `satisfies` on each map carries the exhaustiveness on its own.
 */

type Trace = Messages['trace']

/** A tool's risk class. An unrecognised one says so rather than printing its token. */
export const riskLabel = (t: Trace, risk: string): string =>
  risk in t.risk ? t.risk[risk as RiskClass] : t.riskUnknown

/**
 * What the model was allowed to treat as instruction rather than data. The trust boundary is
 * what this panel exists to show, so an unrecognised label says it is unrecognised rather
 * than rendering nothing and letting a segment look unattributed.
 */
export const trustLabel = (t: Trace, label: string): string =>
  label in t.trust ? t.trust[label as TrustLabel] : t.trustUnknown

/** The budget dimension a warning is about. */
export const dimensionLabel = (t: Trace, dimension: string): string =>
  dimension in t.dimension ? t.dimension[dimension as BudgetDimension] : t.dimensionUnknown

/**
 * A relevance score is four decimals wherever it appears, through `Intl` rather than
 * `toFixed` so the separator is the reader's (PRD-27 F-2). Not copy, so not in the catalogue.
 */
export const SCORE_DIGITS: Intl.NumberFormatOptions = { minimumFractionDigits: 4, maximumFractionDigits: 4 }
